import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Fade } from '@mui/material';
import MDButton from 'components/MDButton';
import {
  AddCircleOutline,
  DeleteSweepOutlined,
  FilterAltOutlined,
  LinkOutlined,
  CheckCircleOutlined
} from '@mui/icons-material';
import { useAutomationContext } from '../context/AutomationContext';
import ConditionGroup from '../components/ConditionBuilder/ConditionGroup';
import { SectionCard, EmptyState } from '../components/StepSection';
import { optionCardSx, statusColors, dangerText, FONT } from '../styles/wizardSx';

const GROUP_LOGIC_OPTIONS = [
  {
    value: 'AND',
    kind: 'success',
    title: 'ALL Groups (AND)',
    helper: 'Every condition group must be true'
  },
  {
    value: 'OR',
    kind: 'warning',
    title: 'ANY Group (OR)',
    helper: 'At least one group must be true'
  }
];

// Edits the automation's gate (wizard state) by default. Given `groups` and
// `onChange`, it edits those instead: the flow builder's branch conditions.
function ConditionsStep({ groups, groupLogic, onChange }) {
  const context = useAutomationContext();
  const controlled = Array.isArray(groups);
  const state = controlled ? { conditionGroups: groups, groupLogic: groupLogic || 'AND' } : context.state;
  const emit = (patch) => onChange({ groups: state.conditionGroups, groupLogic: state.groupLogic, ...patch });
  const actions = controlled
    ? {
      addConditionGroup: (group) => emit({ groups: [...state.conditionGroups, group] }),
      setConditionGroups: (next) => emit({ groups: next }),
      updateConditionGroup: (id, updates) => emit({ groups: state.conditionGroups.map((g) => (g.id === id ? { ...g, ...updates } : g)) }),
      removeConditionGroup: (id) => emit({ groups: state.conditionGroups.filter((g) => g.id !== id) }),
      setGroupLogic: (value) => emit({ groupLogic: value }),
    }
    : context.actions;

  const handleAddGroup = () => {
    const newGroup = {
      id: `group_${Date.now()}`,
      name: `Condition Group ${state.conditionGroups.length + 1}`,
      logic: 'AND',
      logicMode: 'simple',
      customLogic: '',
      conditions: []
    };
    actions.addConditionGroup(newGroup);
  };

  const handleClearAll = () => {
    actions.setConditionGroups([]);
  };

  if (state.conditionGroups.length === 0) {
    return (
      <EmptyState
        icon={<FilterAltOutlined />}
        title="No conditions configured"
        description="This automation will run on every trigger event. Add a condition group to narrow it down."
        action={
          <MDButton
            variant="gradient"
            color="info"
            startIcon={<AddCircleOutline />}
            onClick={handleAddGroup}
          >
            Add Your First Condition
          </MDButton>
        }
      />
    );
  }

  return (
    <Box>
      {state.conditionGroups.map((group, index) => (
        <Fade in key={group.id} timeout={300 + index * 100}>
          <Box sx={{ mb: 2.5 }}>
            <ConditionGroup
              group={group}
              groupIndex={index}
              onUpdate={(updates) => actions.updateConditionGroup(group.id, updates)}
              onRemove={() => actions.removeConditionGroup(group.id)}
            />
          </Box>
        </Fade>
      ))}

      {state.conditionGroups.length > 1 && (
        <SectionCard
          icon={<LinkOutlined fontSize="small" />}
          title="Connect Groups"
          helper="How the condition groups combine."
          sx={{ my: 3 }}
        >
          <Box role="radiogroup" aria-label="Combine condition groups" sx={{ display: 'flex', gap: 1.5 }}>
            {GROUP_LOGIC_OPTIONS.map((option) => {
              const selected = state.groupLogic === option.value;
              return (
                <Box
                  key={option.value}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={() => actions.setGroupLogic(option.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      actions.setGroupLogic(option.value);
                    }
                  }}
                  sx={(theme) => ({ flex: 1, p: 2, ...optionCardSx(theme, { selected, kind: option.kind }) })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: FONT.body,
                          fontWeight: 600,
                          color: selected ? statusColors(theme, option.kind).text : 'text.primary'
                        })}
                      >
                        {option.title}
                      </Typography>
                      <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                        {option.helper}
                      </Typography>
                    </Box>
                    {selected && (
                      <CheckCircleOutlined
                        sx={(theme) => ({ color: statusColors(theme, option.kind).text, fontSize: 20 })}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<AddCircleOutline />}
          onClick={handleAddGroup}
        >
          Add Condition Group
        </MDButton>

        <MDButton
          variant="text"
          color="error"
          startIcon={<DeleteSweepOutlined />}
          onClick={handleClearAll}
          sx={(theme) => ({ color: dangerText(theme) })}
        >
          Clear All
        </MDButton>
      </Box>
    </Box>
  );
}

ConditionsStep.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object),
  groupLogic: PropTypes.string,
  onChange: PropTypes.func,
};

ConditionsStep.defaultProps = { groups: undefined, groupLogic: 'AND', onChange: undefined };

export default ConditionsStep;
